/**
 * DOCUMENTATION:
 * Component: AIProvider
 * Purpose: Global state management for AI interactions (Mentor Sidebar, RAG Search).
 * * * Note for Junior Developers:
 * - 'Context' allows the AI state to persist globally. This means the chat history
 * won't disappear when a student navigates between different lesson pages.
 * - We must export the "setters" (setMessages, setIsThinking) so that our
 * specialized streaming logic in the Sidebar can update the UI in real-time.
 */

import React, { createContext, useContext, useState } from 'react';

const AIContext = createContext();

export const AIProvider = ({ children }) => {
    // 1. Sidebar Visibility State
    const [isSidebarOpen, setSidebarOpen] = useState(false);

    // 2. Chat History State
    const [messages, setMessages] = useState([]);

    // 3. Loading/Thinking State
    const [isThinking, setIsThinking] = useState(false);

    /**
     * Optional: Voice Synthesis for accessibility.
     * Can be triggered after an AI response is fully generated.
     */
    const speak = (text) => {
        if (!window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        const msg = new SpeechSynthesisUtterance(text);
        msg.rate = 1.0;
        window.speechSynthesis.speak(msg);
    };

    /**
     * askAI (Legacy/Simulated Logic)
     * Useful for simple non-streaming tests or background tasks.
     */
    const askAI = async (prompt, context = "general") => {
        setIsThinking(true);

        const userMsg = { role: 'user', content: prompt };
        setMessages(prev => [...prev, userMsg]);

        try {
            // Simulated delay for non-streaming responses
            setTimeout(() => {
                const aiMsg = {
                    role: 'assistant',
                    content: `As your ProDev Mentor, I've analyzed your query about ${context}. Based on our curriculum...`
                };
                setMessages(prev => [...prev, aiMsg]);
                setIsThinking(false);
            }, 1500);

        } catch (error) {
            console.error("AI Retrieval Error:", error);
            setIsThinking(false);
        }
    };

    /**
     * Resets the conversation history.
     */
    const clearChat = () => setMessages([]);

    return (
        <AIContext.Provider value={{
            // State Values
            isSidebarOpen,
            messages,
            isThinking,

            // State Setters (Required for Streaming RAG logic)
            setSidebarOpen,
            setMessages,
            setIsThinking,

            // Helper Functions
            askAI,
            clearChat,
            speak
        }}>
            {children}
        </AIContext.Provider>
    );
};

/**
 * Custom hook for easy access to AI context.
 * Usage: const { messages, setMessages } = useAI();
 */
export const useAI = () => {
    const context = useContext(AIContext);
    if (!context) {
        throw new Error("useAI must be used within an AIProvider wrapper in App.js");
    }
    return context;
};
